"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Heart, Lock } from "lucide-react";
import type { TenantSettingsDto } from "@/types/tenant";
import type { SunnyNavItem } from "@/types/sunny";

const STATION_TIMEZONE = "America/Phoenix";

function formatStationDateTime(date: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: STATION_TIMEZONE,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(date);
}

interface SunnyFooterProps {
  tenant: TenantSettingsDto;
  footerItems: SunnyNavItem[];
}

export function SunnyFooter({ tenant, footerItems }: SunnyFooterProps) {
  const currentYear = new Date().getFullYear();
  const [phoenixTime, setPhoenixTime] = useState(() => formatStationDateTime());
  const version = process.env.NEXT_PUBLIC_APP_VERSION;

  const settings = tenant.settings;
  const siteName = (settings.siteName as string | undefined) || tenant.name || "Sunny 106.5";
  const logoCircleUrl = (settings.logoCircleUrl as string | undefined) || (settings.logoUrl as string | undefined);
  const tagline = (settings.siteTagline as string | undefined) || (settings.tagline as string | undefined) || "Palm Springs Radio & Events";

  useEffect(() => {
    const timer = setInterval(() => {
      setPhoenixTime(formatStationDateTime());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const footerLinks = [...footerItems]
    .filter((link) => link.placement === "FOOTER" && (link.url || "").trim())
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  const openCookiePreferences = () => {
    if (typeof window === "undefined") return;
    const storageKey = `cookie-consent:${tenant.slug}`;
    window.localStorage.removeItem(storageKey);
    window.dispatchEvent(new CustomEvent("sunny:open-cookie-preferences"));
  };

  const linkClassName =
    "text-sm text-slate-700 hover:text-primary hover:underline transition-colors underline-offset-2";

  return (
    <footer className="mt-auto border-t border-slate-200 bg-slate-100">
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link href="/" className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white shadow-sm">
                {logoCircleUrl ? (
                  <img
                    src={logoCircleUrl}
                    alt={`${siteName} logo`}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-lg font-bold text-primary">{siteName.charAt(0)}</span>
                )}
              </div>
              <span className="font-logo text-xl font-bold text-slate-900">{siteName}</span>
            </Link>
            <p className="mb-4 text-sm text-slate-700">{tagline}</p>
            <p className="text-sm text-slate-500">Palm Springs time: {phoenixTime}</p>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-slate-900">Quick Links</h3>
            <ul className="space-y-2">
              {footerLinks.map((link) => {
                const external = link.url.startsWith("http");
                return (
                  <li key={link.id}>
                    {external ? (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClassName}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link href={link.url} className={linkClassName}>
                        {link.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-slate-900">Connect</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/contact" className={linkClassName}>
                  Contact Us
                </Link>
              </li>
              <li>
                <Link href="/newsletter" className={linkClassName}>
                  Newsletter
                </Link>
              </li>
              <li>
                <Link href="/member/login" className={linkClassName}>
                  Lucky Duck Club
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-slate-900">Resources</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/privacy" className={linkClassName}>
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/terms" className={linkClassName}>
                  Terms of Service
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookiePreferences}
                  className={`${linkClassName} text-left`}
                >
                  Cookie Preferences
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
              <span>
                &copy; {currentYear} {siteName}. All rights reserved.
              </span>
              {version && (
                <>
                  <span className="text-slate-400">|</span>
                  <span className="text-slate-500">v{version}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-4">
              <p className="flex items-center gap-1 text-sm text-slate-600">
                Made with <Heart className="h-4 w-4 fill-current text-red-500" /> by{" "}
                <a
                  href="https://1lpro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary hover:text-primary/80 hover:underline underline-offset-2"
                >
                  1LPro.com
                </a>
              </p>
              <Link
                href="/admin"
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-200 hover:text-primary"
                title="Admin Login"
                aria-label="Admin Login"
              >
                <Lock className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
